import { useEffect, useState, VFC } from 'react';
import styled from 'styled-components';

import JsonSchemaSelect, { BooleanSelect, Option } from 'dashboard/components/JsonSchemaSelect';
import {
  ExpressionRuleValueAll,
  ExpressionVar,
  JsonLogicField,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import { VariableType } from 'dashboard/models/workflow-version';
import Input from 'design-system/components/Input';
import { paragraphMixins } from 'design-system/mixins/text';
import { JsonSchemaCustomTypes } from 'lib/fields';
import usePrevious from 'lib/hooks/usePrevious';
import { JsonSchema, JsonSchemaBasicTypes, JsonSchemaTypes } from 'lib/json-schema';

export const StyledInput = styled(Input)`
  height: ${({ theme }) => theme.element.height.xs};
  ${paragraphMixins.md};
`;

interface TermProps {
  allowedTypes?: (JsonSchemaTypes | JsonSchemaCustomTypes)[];
  allowedArrayTypes?: (JsonSchemaBasicTypes | JsonSchemaTypes.Object)[];
  id: string;
  hasError?: boolean;
  jsonSchema: JsonSchema;
  operator: WorkflowOperatorKey;
  term: ExpressionRuleValueAll;
  termType: RuleValueTypes;
  tokensToDisplayValue: Record<string, string>;
  variableSchema: JsonSchema;
  variableType?: VariableType;
  varTokensToDisplayValue: Record<string, string>;
  setVariableValue?: (value: ExpressionRuleValueAll) => void;
}

const Term: VFC<TermProps> = ({
  allowedTypes,
  allowedArrayTypes,
  id,
  hasError,
  jsonSchema,
  operator,
  term,
  termType,
  tokensToDisplayValue,
  variableSchema,
  variableType,
  varTokensToDisplayValue,
  setVariableValue,
}) => {
  const [field, setField] = useState<string | string[]>((term as JsonLogicField)?.var || '');
  const [variable, setVariable] = useState<string | string[]>(
    (term as ExpressionVar)?.logicvar || '',
  );

  const previousVariableType = usePrevious(variableType);
  const previousTermType = usePrevious(termType);
  const previousOperator = usePrevious(operator);
  useEffect(() => {
    if (
      (previousOperator && previousOperator !== operator) ||
      (previousVariableType && previousVariableType !== variableType) ||
      (previousTermType && previousTermType !== termType)
    ) {
      setField('');
      setVariable('');
    }
  }, [previousOperator, operator, previousVariableType, variableType, previousTermType, termType]);

  switch (termType) {
    case RuleValueTypes.Field:
      return (
        <JsonSchemaSelect
          id={`${id}__field`}
          ariaLabel='Select JSON schema field for expression term'
          inputValue={field}
          jsonSchema={jsonSchema}
          allowedTypes={allowedTypes}
          allowedArrayTypes={allowedArrayTypes}
          tokensToDisplayValue={tokensToDisplayValue}
          hasError={hasError}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onFieldSelect={(option: Option) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setVariableValue({ var: option.value });
          }}
          onValueChange={setField}
        />
      );
    case RuleValueTypes.Value: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const isBoolean = allowedTypes.every((type) => {
        return JsonSchemaTypes.Boolean === type;
      });

      if (isBoolean) {
        return (
          <BooleanSelect
            id={`${id}__value`}
            inputValue={variable as string}
            hasError={hasError}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setVariableValue={setVariableValue}
          />
        );
      }
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const isNumber = allowedTypes.every((type) => {
        return JsonSchemaTypes.Number === type || JsonSchemaTypes.Integer === type;
      });
      return (
        <StyledInput
          aria-label={isNumber ? 'Enter numeric value' : 'Enter text value'}
          id={`${id}__value`}
          value={term as string | number}
          onChange={(e) => {
            // If multiple JsonSchemaTypes are allowed for the input,
            // then we let user type in a string and do our best to fuzzy match it in the backend.
            const newVal = isNumber ? parseInt(e.target.value, 10) : e.target.value;
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setVariableValue(newVal);
          }}
          type={isNumber ? 'number' : 'text'}
          hasError={hasError}
        />
      );
    }
    case RuleValueTypes.Variable:
      return (
        <JsonSchemaSelect
          id={`${id}__variable`}
          ariaLabel='Select JSON schema variable for expression term'
          inputValue={variable}
          jsonSchema={variableSchema}
          allowedTypes={allowedTypes}
          allowedArrayTypes={allowedArrayTypes}
          tokensToDisplayValue={varTokensToDisplayValue}
          hasError={hasError}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onFieldSelect={(option: Option) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setVariableValue({ logicvar: option.value });
          }}
          onValueChange={setVariable}
        />
      );
    default:
      return <></>;
  }
};

export default Term;
